import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  linkedSignal,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import {
  EventqAutoProcessPopover,
  EventqBrowserTabBar,
  EventqBooleanToggle,
  EventqToastContainer,
  EventqToastService,
  EventqUserBar,
  PrefixUrlPipe,
  SharedWorkerService,
  EventqActionLoader,
} from '@ecam/eventq-components';
import type { AutoProcessConfirmMode } from '@ecam/eventq-components';
import { BROWSER_TAB_MAX_COUNT } from '../../../core/models/browser-tab-max';
import { AgentAccessService } from '../../../core/services/agent-access.service';
import { AgentPreferencesService } from '../../../core/services/agent-preferences.service';
import { AuthSessionService } from '../../../core/services/auth-session.service';
import { BrowserTabNavigationService } from '../../../core/services/browser-tab-navigation.service';
import { BrowserTabPopoutService } from '../../../core/services/browser-tab-popout.service';
import { BrowserTabService } from '../../../core/services/browser-tab.service';
import { EventHandleService } from '../../../core/services/event-handle.service';
import { PageStateInitService } from '../../../core/services/page-state-init.service';
import { ProcessingTimerService } from '../../../core/services/processing-timer.service';
import { environment } from '../../../../environments/environment';
import {
  eventIdFromProcessingRoutePath,
  isEventsLandingRoutePath,
} from '../../../core/routing/browser-tab-routes';

@Component({
  selector: 'app-agent-shell',
  imports: [
    RouterOutlet,
    EventqBrowserTabBar,
    EventqBooleanToggle,
    EventqToastContainer,
    EventqUserBar,
    EventqAutoProcessPopover,
    PrefixUrlPipe,
    EventqActionLoader,
  ],
  templateUrl: './agent-shell.html',
  styleUrl: './agent-shell.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-tab-expanded]': 'chromeCompact()',
  },
})
export class AgentShell {
  private readonly auth = inject(AuthSessionService);
  private readonly access = inject(AgentAccessService);
  private readonly preferences = inject(AgentPreferencesService);
  private readonly toast = inject(EventqToastService);
  private readonly router = inject(Router);
  private readonly popout = inject(BrowserTabPopoutService);
  private readonly tabNavigation = inject(BrowserTabNavigationService);
  private readonly eventHandles = inject(EventHandleService);
  private readonly processingTimers = inject(ProcessingTimerService);
  readonly browserTabs = inject(BrowserTabService);
  private readonly pageInit = inject(PageStateInitService);
  private readonly sharedWorker = inject(SharedWorkerService);

  /**
   * True when the SharedWorker confirms this is a duplicate EventQ window.
   * The shell shows a blocking overlay instead of the normal workspace.
   * Condition: sharedWorker feature enabled + connected + not primary lease holder.
   */
  readonly isDuplicateWindow = computed(
    () =>
      environment.features.sharedWorker &&
      this.sharedWorker.connected() &&
      this.sharedWorker.isPrimary() === false,
  );

  /**
   * FUTURE: tab → new browser window. Controlled by `environment.features.enableTabPopout`.
   * Keep false until popout chrome / session handoff is ready to ship.
   */
  readonly tabPopoutEnabled = environment.features.enableTabPopout;

  /**
   * In-window expanded tabs hide logo / auto-process / user chrome.
   * Popout windows keep the full header so every shell control is available.
   */
  readonly chromeCompact = computed(() => this.browserTabs.expanded());

  readonly autoProcess = this.preferences.autoProcess;
  readonly autoProcessConfirm = signal<AutoProcessConfirmMode | null>(null);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /**
   * Logout clears the session before `/login` replaces this shell.
   * Keep the last real address so the bar does not flash a placeholder.
   */
  readonly email = linkedSignal<string | null, string>({
    source: this.auth.email,
    computation: (email, previous) => email || previous?.value || '',
  });

  /** Popouts render immediately. The main window waits until workspace restore finishes. */
  readonly workspaceVisible = computed(
    () => this.browserTabs.isPopoutWindow() || this.pageInit.workspaceReady(),
  );

  readonly workspaceInitFailed = this.pageInit.workspaceInitFailed;

  readonly canAddTab = computed(
    () =>
      this.workspaceVisible() &&
      this.access.canAddBrowserTab() &&
      this.browserTabs.canAddTab(),
  );

  readonly showAutoProcess = computed(() => this.access.canAccessEventsDashboard());

  /**
   * Close is only available on the events landing page and only when more than
   * one workspace tab is open (never while processing / loading / patrolling).
   */
  readonly closeableTabIds = computed(() => {
    const tabs = this.browserTabs.tabs();
    if (tabs.length <= 1 || this.browserTabs.isPopoutWindow()) {
      return [] as string[];
    }

    // Depend on active page state so routePath updates recompute closeability.
    this.browserTabs.activePageState();

    return tabs
      .filter((tab) => isEventsLandingRoutePath(this.browserTabs.getPageState(tab.id).routePath))
      .map((tab) => tab.id);
  });

  readonly displayTabs = computed(() => {
    if (!this.workspaceVisible()) {
      return [];
    }

    this.processingTimers.nowMs();
    const tabs = this.browserTabs.tabs();
    const activeId = this.browserTabs.activeTabId();
    const isLoadingRoute = this.currentUrl().includes('/load/');

    return tabs.map((tab) => {
      const runtimeLabel = this.processingTimers.runtimeLabelForTab(tab.id);
      const awaitingEventName =
        tab.label === 'New Event' &&
        eventIdFromProcessingRoutePath(this.browserTabs.getPageState(tab.id).routePath) != null;
      const label =
        isLoadingRoute && tab.id === activeId
          ? 'Loading...'
          : awaitingEventName
            ? 'Loading Event'
            : tab.label;
      return { ...tab, label, runtimeLabel: runtimeLabel ?? null };
    });
  });

  onLogout(): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.auth.logout();
  }

  onTabSelect(id: string): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    void this.tabNavigation.navigateToTab(id);
  }

  onTabAdd(): void {
    if (
      this.browserTabs.isPopoutWindow() ||
      !this.canAddTab() ||
      this.browserTabs.claimInteractionLocked()
    ) {
      return;
    }

    void this.addTabWorkspace();
  }

  onTabClose(tabId: string): void {
    if (
      this.browserTabs.claimInteractionLocked() ||
      this.browserTabs.isPopoutWindow() ||
      this.browserTabs.tabs().length <= 1
    ) {
      return;
    }

    if (!isEventsLandingRoutePath(this.browserTabs.getPageState(tabId).routePath)) {
      return;
    }

    this.tabNavigation.closeTabWorkspace(tabId);
  }

  private async addTabWorkspace(): Promise<void> {
    const tabId = this.browserTabs.addTab();
    if (!tabId) {
      this.toast.show('notice', {
        title: 'Open work limit reached',
        subtitle: `You can open up to ${BROWSER_TAB_MAX_COUNT} work items at once.`,
        tone: 'error',
      });
      return;
    }

    if (!this.autoProcess()) {
      await this.tabNavigation.navigateToTab(tabId);
      return;
    }

    const endClaim = this.browserTabs.beginClaimInteraction(tabId);
    try {
      const claimed = await this.eventHandles.claimNextForTab(tabId);
      if (!claimed?.ok) {
        await this.tabNavigation.navigateToTab(tabId);
        const workLimit = claimed && !claimed.ok && claimed.conflict.code === 'WORK_LIMIT';
        this.toast.show('notice', {
          title: workLimit ? 'Open work limit reached' : 'No available events',
          subtitle: workLimit
            ? claimed.conflict.message
            : 'Opened a blank tab — every waiting event is already held.',
          tone: 'error',
        });
        return;
      }

      await this.tabNavigation.openClaimedProcessEvent(claimed.handle.eventId);
    } finally {
      endClaim();
    }
  }

  onTabExpand(): void {
    if (this.browserTabs.claimInteractionLocked() || !this.tabPopoutEnabled) {
      return;
    }

    if (this.browserTabs.isPopoutWindow()) {
      window.close();
      return;
    }

    this.popout.moveActiveTabToNewWindow(this.currentUrl(), this.auth.accessToken());
  }

  onAutoProcessToggleChange(checked: boolean): void {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    const currentlyEnabled = this.autoProcess();
    if (checked === currentlyEnabled) {
      return;
    }
    this.autoProcessConfirm.set(checked ? 'on' : 'off');
  }

  onAutoProcessConfirmClose(): void {
    this.autoProcessConfirm.set(null);
  }

  async onAutoProcessConfirm(enabled: boolean): Promise<void> {
    if (this.browserTabs.claimInteractionLocked()) {
      return;
    }
    this.autoProcessConfirm.set(null);
    this.preferences.setAutoProcess(enabled);
    if (!enabled) {
      this.toast.autoProcessOff();
      return;
    }

    this.toast.autoProcessOn();
    await this.claimNextWhenIdle();
  }

  private claimingOnAutoProcess = false;

  /**
   * Landing may retain a stale activeEventId without a live handle — clear it
   * so it does not count as open work when Auto-Process turns on.
   */
  private clearStaleActiveEventOnLanding(): void {
    const state = this.browserTabs.activePageState();
    if (
      state.activeEventId &&
      !state.eventHandleId &&
      isEventsLandingRoutePath(state.routePath)
    ) {
      this.browserTabs.updateActivePageState({ activeEventId: null });
    }
  }

  private async claimNextWhenIdle(): Promise<void> {
    if (this.claimingOnAutoProcess) {
      return;
    }

    this.claimingOnAutoProcess = true;
    try {
      this.clearStaleActiveEventOnLanding();
      if (this.browserTabs.openWorkCount() > 0) {
        return;
      }

      const tabId = this.browserTabs.activeTabId();
      if (!tabId) {
        return;
      }

      const endClaim = this.browserTabs.beginClaimInteraction(tabId);
      try {
        const claimed = await this.eventHandles.claimNextForTab(tabId);
        if (!claimed?.ok) {
          const workLimit = claimed && !claimed.ok && claimed.conflict.code === 'WORK_LIMIT';
          this.toast.show('notice', {
            title: workLimit ? 'Open work limit reached' : 'No available events',
            subtitle: workLimit
              ? claimed.conflict.message
              : 'Every waiting event is already held by another agent or tab.',
            tone: 'error',
          });
          return;
        }

        await this.tabNavigation.openClaimedProcessEvent(claimed.handle.eventId);
      } finally {
        endClaim();
      }
    } finally {
      this.claimingOnAutoProcess = false;
    }
  }
}
